// Глобальный страж stale-session ошибок WalletConnect SDK.
//
// Проблема: @walletconnect/ethereum-provider при получении relay-сообщения
// для топика, которого нет в локальной сессии, бросает синхронную ошибку
// «No matching key. session topic doesn't exist: <hash>» прямо из onRelayMessage.
// Эта ошибка вылетает мимо наших try/catch и event-listener'ов, попадает в
// глобальный handler браузера и в дев-режиме рисует красный CRA error overlay.
//
// Сама ошибка нефатальная — у нас уже нет этой сессии, SDK просто шумит.
// Поэтому глушим её глобально + триггерим очистку, чтобы следующий
// getWCProvider() поднял чистый инстанс.

import { purgeWCSession, isStaleSessionError, hasLiveWCSession } from './walletConnect';

let cleanupScheduled = false;

const scheduleCleanup = () => {
    if (cleanupScheduled) return;
    cleanupScheduled = true;
    // Делаем microtask-задержку, чтобы не дёргать SDK прямо в его же error-стеке
    queueMicrotask(() => {
        // Если есть живая сессия — ошибка была эхом от СТАРОГО мёртвого топика.
        // purge снёс бы и рабочую сессию (кошелёк «отваливался сам») — не чистим.
        if (hasLiveWCSession()) {
            cleanupScheduled = false;
            return;
        }
        purgeWCSession()
            .catch(() => {})
            .finally(() => { cleanupScheduled = false; });
    });
};

// Падение внутри самого SDK при обработке chainChanged: провайдер зовёт
// switchEthereumChain для сети, для которой у него нет RPC-конфига, и падает на
// `undefined.request`. Ошибка нефатальная (кошелёк уже переключён), но в дев-режиме
// рисует красный overlay поверх аппки. Узнаём её по стеку, чтобы не глушить
// посторонние TypeError'ы.
const isChainSwitchNoise = (errLike) => {
    const msg = String(errLike?.message || errLike || '');
    const stack = String(errLike?.stack || '');
    // Chrome: "Cannot read properties of undefined (reading 'request')"
    // Safari: "undefined is not an object (evaluating 'this.getProvider(n).request')"
    const looksLikeMissingProvider =
        /reading 'request'/.test(msg) ||
        /getProvider\([^)]*\)\.request/.test(msg) ||
        (/undefined is not an object/.test(msg) && /\.request/.test(msg));
    const fromWcInternals =
        /switchEthereumChain|setChainId|onSessionEventRequest|getProvider/.test(msg + stack);
    return looksLikeMissingProvider && fromWcInternals;
};

const matches = (errLike) => isStaleSessionError(errLike);

// Гасим ошибку, но чистим сессию ТОЛЬКО для stale-session. Для шума при смене
// сети сессия жива — сбрасывать её нельзя, иначе кошелёк отвалится сам собой.
const handle = (errLike, silence) => {
    if (matches(errLike)) {
        silence();
        scheduleCleanup();
        return;
    }
    if (isChainSwitchNoise(errLike)) {
        silence();
    }
};

if (typeof window !== 'undefined') {
    window.addEventListener(
        'error',
        (event) => {
            handle(event.error ?? event.message, () => {
                event.preventDefault();
                event.stopImmediatePropagation?.();
            });
        },
        true,
    );

    window.addEventListener(
        'unhandledrejection',
        (event) => {
            handle(event.reason, () => event.preventDefault());
        },
        true,
    );
}

export {};
